import React from "react";
import { useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";
import ChatBubbleOutlineRoundedIcon from "@material-ui/icons/ChatBubbleOutlineRounded";

import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => {
	const isDark = theme.palette.type === "dark";
	const isRTL = theme.direction === "rtl";

	const fg       = isDark ? "rgba(255,255,255,0.92)" : "#0f1614";
	const fgMuted  = isDark ? "rgba(255,255,255,0.6)"  : "rgba(15,22,20,0.6)";
	const fgFaint  = isDark ? "rgba(255,255,255,0.32)" : "rgba(15,22,20,0.36)";
	const accent   = isDark ? "#2ee37a" : "#128c7e";
	const cardBg   = isDark ? "#13181a" : "#ffffff";
	const hair     = isDark ? "rgba(255,255,255,0.06)" : "rgba(15,22,20,0.06)";

	return {
		root: {
			minHeight: "100vh",
			width: "100%",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			padding: "40px 20px",
			position: "relative",
			overflow: "hidden",
			background: isDark
				? `radial-gradient(900px 700px at 14% 16%, rgba(46,227,122,0.10), transparent 60%),
				   radial-gradient(700px 600px at 86% 84%, rgba(18,140,126,0.13), transparent 60%),
				   linear-gradient(165deg, #0a1310 0%, #060a08 100%)`
				: `radial-gradient(900px 700px at 14% 16%, rgba(37,211,102,0.14), transparent 60%),
				   radial-gradient(700px 600px at 86% 84%, rgba(18,140,126,0.08), transparent 60%),
				   linear-gradient(165deg, #eef3ec 0%, #e3ecdf 100%)`,
		},

		// Faint dotted grid — same texture as Login brand pane so the two
		// surfaces feel like they live in the same world.
		texture: {
			position: "absolute",
			inset: 0,
			backgroundImage: isDark
				? "radial-gradient(rgba(232,239,216,0.05) 1px, transparent 1px)"
				: "radial-gradient(rgba(10,38,32,0.07) 1px, transparent 1px)",
			backgroundSize: "28px 28px",
			pointerEvents: "none",
			zIndex: 0,
		},

		// Massive italic "404" watermark — same trick as Login's "G".
		// Reads as ambient texture, not a literal error stamp.
		watermark: {
			position: "absolute",
			bottom: -160,
			[isRTL ? "right" : "left"]: -40,
			fontWeight: 400,
			fontStyle: "italic",
			fontSize: 460,
			lineHeight: 0.8,
			letterSpacing: "-0.06em",
			color: isDark ? "rgba(46,227,122,0.07)" : "rgba(18,140,126,0.09)",
			pointerEvents: "none",
			userSelect: "none",
			fontVariationSettings: '"opsz" 144, "SOFT" 100',
			zIndex: 0,
		},

		card: {
			position: "relative",
			zIndex: 1,
			maxWidth: 540,
			width: "100%",
			padding: "48px 44px 40px",
			borderRadius: 24,
			background: cardBg,
			border: `1px solid ${hair}`,
			boxShadow: isDark
				? "0 1px 0 rgba(255,255,255,0.04) inset, 0 32px 64px rgba(0,0,0,0.5)"
				: "0 32px 60px rgba(15,22,20,0.08), 0 4px 12px rgba(15,22,20,0.04)",
			animation: "$cardIn 0.8s 0.1s cubic-bezier(0.2,0.8,0.2,1) both",
			textAlign: "start",
			[theme.breakpoints.down("xs")]: {
				padding: "36px 24px",
				borderRadius: 18,
			},
		},

		brandRow: {
			display: "flex",
			alignItems: "center",
			gap: 11,
			marginBottom: 28,
		},
		markChip: {
			width: 36,
			height: 36,
			borderRadius: 10,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			background: "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
			color: "#ffffff",
			boxShadow: isDark
				? "0 0 0 1px rgba(46,227,122,0.22), 0 6px 18px rgba(46,227,122,0.18)"
				: "0 0 0 1px rgba(255,255,255,0.6) inset, 0 6px 18px rgba(18,140,126,0.18)",
			"& svg": { fontSize: 18 },
		},
		markText: {
			fontWeight: 600,
			fontSize: 18,
			letterSpacing: "-0.012em",
			color: fg,
			lineHeight: 1,
		},

		errorCode: {
			fontSize: 11,
			fontWeight: 700,
			letterSpacing: "0.22em",
			textTransform: "uppercase",
			color: accent,
			margin: 0,
			marginBottom: 18,
			display: "inline-flex",
			alignItems: "center",
			gap: 12,
			"&::before": {
				content: '""',
				width: 28,
				height: 1,
				background: accent,
			},
		},

		headline: {
			fontWeight: 500,
			fontSize: "clamp(34px, 4.5vw, 48px)",
			lineHeight: 1.05,
			letterSpacing: "-0.022em",
			color: fg,
			margin: 0,
			fontVariationSettings: '"opsz" 72, "SOFT" 40',
			"& em": {
				fontStyle: "italic",
				fontWeight: 400,
				color: accent,
				fontVariationSettings: '"opsz" 72, "SOFT" 100',
			},
		},

		body: {
			fontSize: 15,
			fontWeight: 400,
			lineHeight: 1.6,
			color: fgMuted,
			margin: 0,
			marginTop: 16,
			maxWidth: "44ch",
		},

		actions: {
			marginTop: 32,
			display: "flex",
			alignItems: "center",
			gap: 14,
			flexWrap: "wrap",
		},
		cta: {
			height: 48,
			padding: "0 22px",
			borderRadius: 12,
			fontWeight: 700,
			fontSize: 14,
			letterSpacing: "0.01em",
			textTransform: "none",
			color: "#ffffff",
			background: "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
			boxShadow: isDark
				? "0 12px 24px rgba(46,227,122,0.22), inset 0 1px 0 rgba(255,255,255,0.18)"
				: "0 14px 28px rgba(18,140,126,0.28), inset 0 1px 0 rgba(255,255,255,0.25)",
			transition: "transform 0.18s ease, filter 0.18s ease",
			"&:hover": {
				background: "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
				transform: "translateY(-1px)",
				filter: "brightness(1.04)",
			},
		},
		ghost: {
			height: 48,
			padding: "0 18px",
			borderRadius: 12,
			fontWeight: 600,
			fontSize: 14,
			textTransform: "none",
			color: fg,
			background: "transparent",
			border: `1px solid ${hair}`,
			transition: "border-color 0.18s ease, background 0.18s ease",
			"&:hover": {
				background: "transparent",
				borderColor: accent,
			},
		},

		footnote: {
			marginTop: 28,
			paddingTop: 22,
			borderTop: `1px solid ${hair}`,
			fontSize: 11,
			fontWeight: 600,
			letterSpacing: "0.16em",
			textTransform: "uppercase",
			color: fgFaint,
			display: "flex",
			alignItems: "center",
			gap: 10,
		},

		"@keyframes cardIn": {
			from: { opacity: 0, transform: "translateY(10px) scale(0.985)" },
			to:   { opacity: 1, transform: "translateY(0) scale(1)" },
		},
	};
});

const NotFound = () => {
	const classes = useStyles();
	const history = useHistory();

	return (
		<div className={classes.root}>
			<div className={classes.texture} aria-hidden="true" />
			<div className={classes.watermark} aria-hidden="true">404</div>

			<div className={classes.card}>
				<div className={classes.brandRow}>
					<span className={classes.markChip}>
						<ChatBubbleOutlineRoundedIcon />
					</span>
					<span className={classes.markText}>Whapiz</span>
				</div>

				<p className={classes.errorCode}>
					{i18n.t("notFound.code", { defaultValue: "Error 404" })}
				</p>

				<h1 className={classes.headline}>
					{i18n.t("notFound.h1", { defaultValue: "We can't find " })}
					<em>
						{i18n.t("notFound.h1em", { defaultValue: "that page" })}
					</em>
					.
				</h1>

				<p className={classes.body}>
					{i18n.t("notFound.body", {
						defaultValue:
							"It may have moved, or you might have followed an old link. Head back to your workspace and pick up where you left off.",
					})}
				</p>

				<div className={classes.actions}>
					<Button
						variant="contained"
						className={classes.cta}
						onClick={() => history.push("/tickets")}
						disableElevation
					>
						{i18n.t("notFound.cta", { defaultValue: "Back to workspace" })}
					</Button>
					<Button
						variant="outlined"
						className={classes.ghost}
						onClick={() => history.goBack()}
					>
						{i18n.t("notFound.back", { defaultValue: "Go back" })}
					</Button>
				</div>

				<p className={classes.footnote}>
					<span>{i18n.t("notFound.foot", { defaultValue: "Whapiz" })}</span>
					<span style={{ opacity: 0.4 }}>·</span>
					<span>
						{i18n.t("notFound.foot2", { defaultValue: "WhatsApp CRM" })}
					</span>
				</p>
			</div>
		</div>
	);
};

export default NotFound;
